import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Box, Paper, Typography, Alert, Snackbar, Button, CircularProgress } from '@mui/material';
import MonthlyCalendar from '../components/Schedule/MonthlyCalendar';
import ScheduleFilters from '../components/Schedule/ScheduleFilters';
import ShiftForm from '../components/Schedule/ShiftForm';
import { AuthContext } from '../contexts/AuthContext';
import { getUsers } from '../services/user';
import { 
  createSchedule, 
  updateSchedule, 
  deleteSchedule, 
  getSchedules,
  approveSchedule,
  rejectSchedule,
  approveAllMonthSchedules
} from '../services/schedule';
import { format } from 'date-fns';
import { addLog as createLog } from '../services/logs';

// Mapowanie ID lokalizacji na pełne nazwy
const locationNames = {
  'PAN': 'Panorama M&M GOLD',
  'VVS': 'Panorama VVS',
  'MODO': 'Modo M&M GOLD',
  'KLIF': 'Klif M&M GOLD',
  'GP': 'Galeria Północna M&M GOLD',
  'SB': 'Stary Browar M&M GOLD',
  'MB': 'Marbella'
};

const ShiftStatus = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

const Schedule = () => {
  const { currentUser, userRole, logout } = useContext(AuthContext);
  const isAdmin = useMemo(() => userRole === 'admin', [userRole]);

  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employees, setEmployees] = useState([]);
  const [allShifts, setAllShifts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [shiftFormOpen, setShiftFormOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [needsRefresh, setNeedsRefresh] = useState(true);

  const dateParams = useMemo(() => ({
    year: currentDate.getFullYear(),
    month: currentDate.getMonth() + 1
  }), [currentDate]);

  // Pobierz listę pracowników jeśli użytkownik jest adminem
  useEffect(() => {
    if (isAdmin) {
      getUsers()
        .then(users => {
          setEmployees(users.map(user => ({
            id: user.uid,
            name: `${user.firstName} ${user.lastName}`
          })));
        })
        .catch(error => {
          if (error.response?.status === 401) {
            logout();
          }
        });
    }
  }, [isAdmin, logout]);

  // Pobierz zmiany na podstawie filtrów
  const fetchShifts = useCallback(async () => {
    if (!currentUser || !needsRefresh) return;
    setIsLoading(true);

    try {
      const params = {
        ...dateParams,
        ...(currentUser && !isAdmin && { user_id: currentUser.uid })
      };

      console.log('fetchShifts - params:', params);
      const updatedShifts = await getSchedules(params);
      setAllShifts(updatedShifts);
      setNeedsRefresh(false);
    } catch (error) {
      console.error('Błąd podczas pobierania grafiku:', error);
      if (error.response?.status === 401) {
        logout();
      }
      setErrorMessage('Błąd podczas pobierania grafiku');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, isAdmin, logout, dateParams, needsRefresh]);

  // Pobierz dane tylko gdy zmienią się filtry lub użytkownik
  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const handleMonthChange = useCallback((date) => {
    setCurrentDate(date);
    setNeedsRefresh(true);
  }, []);

  const handleShiftClick = (shift) => {
    // Admin może otwierać wszystkie zmiany
    // Zwykli użytkownicy mogą otwierać tylko swoje zmiany i tylko te, które nie są jeszcze zatwierdzone
    if (!isAdmin && (
      shift.user_id !== currentUser.uid || 
      shift.status === 'approved' // Jeśli zmiana jest zatwierdzona, nie można jej edytować
    )) {
      // Pokaż wiadomość informacyjną jeśli próbują otworzyć zatwierdzoną zmianę
      if (shift.user_id === currentUser.uid && shift.status === 'approved') {
        setErrorMessage('Nie możesz edytować zatwierdzonej zmiany');
      }
      return;
    }

    setSelectedShift(shift);
    setSelectedDate(new Date(shift.date));
    setShiftFormOpen(true);
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    setSelectedShift(null);
    setShiftFormOpen(true);
  };

  const handleShiftDelete = async (shiftId) => {
    try {
      const shift = allShifts.find(s => s.id === shiftId);
      if (!shift) {
        setErrorMessage('Nie znaleziono zmiany');
        return;
      }

      await deleteSchedule(shiftId);
      setNeedsRefresh(true);
      
      try {
        // Dodaj log o usunięciu zmiany
        await createLog({
          action: 'schedule_delete',
          entity_type: 'schedule',
          entity_id: shiftId,
          user_id: currentUser.uid,
          details: JSON.stringify({
            date: shift.date,
            start_time: shift.start_time,
            end_time: shift.end_time,
            location: shift.location,
            user_id: shift.user_id,
            status: shift.status,
            deleted_by_id: currentUser.uid
          })
        });
      } catch (logError) {
        console.error('Błąd podczas zapisywania logu:', logError);
        // Nie przerywamy działania jeśli log się nie zapisze
      }

      setSuccessMessage('Zmiana została usunięta');
      setShiftFormOpen(false);
      fetchShifts();
    } catch (error) {
      console.error('Błąd podczas usuwania zmiany:', error);
      setErrorMessage('Nie udało się usunąć zmiany');
    }
  };

  const handleShiftApprove = async (shiftId) => {
    setIsLoading(true);
    try {
      const shift = allShifts.find(shift => shift.id === shiftId);
      if (!shift) {
        setErrorMessage('Nie znaleziono zmiany do zatwierdzenia');
        return;
      }

      // Tylko admin może zatwierdzać zmiany
      if (!isAdmin) {
        setErrorMessage('Nie masz uprawnień do zatwierdzania zmian');
        return;
      }

      await approveSchedule(shiftId);
      
      // Dodaj log
      await createLog({
        action: 'approve_schedule',
        user_id: currentUser.uid,
        user_name: `${currentUser.firstName} ${currentUser.lastName}`,
        details: `Zatwierdzono zmianę dla ${shift.user_name} w dniu ${shift.date} w lokalizacji ${shift.location}`
      });

      setSuccessMessage('Zmiana została zatwierdzona');
      setNeedsRefresh(true);
      setShiftFormOpen(false);
    } catch (error) {
      console.error('Błąd podczas zatwierdzania zmiany:', error);
      setErrorMessage('Błąd podczas zatwierdzania zmiany');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Wersja funkcji do zatwierdzania z poziomu kalendarza
  const handleCalendarShiftApprove = async (shift) => {
    console.log('Próba zatwierdzenia zmiany z kalendarza:', shift);
    if (shift && shift.id) {
      try {
        await handleShiftApprove(shift.id);
        // Odśwież listę zmian natychmiast po zatwierdzeniu
        fetchShifts();
      } catch (error) {
        console.error('Błąd podczas zatwierdzania zmiany z kalendarza:', error);
        setErrorMessage('Nie udało się zatwierdzić zmiany');
      }
    } else {
      console.error('Brak ID zmiany do zatwierdzenia');
      setErrorMessage('Nie można zatwierdzić zmiany: brak ID');
    }
  };

  const handleShiftReject = async (shiftId, rejectionReason = 'Zmiana odrzucona przez administratora') => {
    setIsLoading(true);
    try {
      const shift = allShifts.find(shift => shift.id === shiftId);
      if (!shift) {
        setErrorMessage('Nie znaleziono zmiany do odrzucenia');
        return;
      }

      // Tylko admin może odrzucać zmiany
      if (!isAdmin) {
        setErrorMessage('Nie masz uprawnień do odrzucania zmian');
        return;
      }

      await rejectSchedule(shiftId, rejectionReason);
      
      // Dodaj log
      await createLog({
        action: 'reject_schedule',
        user_id: currentUser.uid,
        user_name: `${currentUser.firstName} ${currentUser.lastName}`,
        details: `Odrzucono zmianę dla ${shift.user_name} w dniu ${shift.date} w lokalizacji ${shift.location}. Powód: ${rejectionReason}`
      });

      setSuccessMessage('Zmiana została odrzucona');
      setNeedsRefresh(true);
      setShiftFormOpen(false);
    } catch (error) {
      console.error('Błąd podczas odrzucania zmiany:', error);
      setErrorMessage('Błąd podczas odrzucania zmiany');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Wersja funkcji do odrzucania z poziomu kalendarza
  const handleCalendarShiftReject = async (shift, rejectionReason) => {
    console.log('Próba odrzucenia zmiany z kalendarza:', shift, 'Powód:', rejectionReason);
    if (shift && shift.id) {
      try {
        await handleShiftReject(shift.id, rejectionReason);
        // Odśwież listę zmian natychmiast po odrzuceniu
        fetchShifts();
      } catch (error) {
        console.error('Błąd podczas odrzucania zmiany z kalendarza:', error);
        setErrorMessage('Nie udało się odrzucić zmiany');
      }
    } else {
      console.error('Brak ID zmiany do odrzucenia');
      setErrorMessage('Nie można odrzucić zmiany: brak ID');
    }
  };

  const handleApproveAllMonth = useCallback(async () => {
    if (!selectedEmployee) return;

    try {
      await approveAllMonthSchedules(
        selectedEmployee,
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        currentUser.uid
      );
      // Dodaj log o zatwierdzeniu wszystkich zmian
      await createLog({
        action: 'schedule_approve_all',
        entity_type: 'schedule',
        entity_id: `${selectedEmployee}_${currentDate.getFullYear()}_${currentDate.getMonth() + 1}`,
        user_id: currentUser.uid,
        details: {
          employee_id: selectedEmployee,
          year: currentDate.getFullYear(),
          month: currentDate.getMonth() + 1,
          approved_by_id: currentUser.uid,
          status: 'approved'
        }
      });
      setSuccessMessage('Wszystkie zmiany zostały zatwierdzone');
      fetchShifts();
    } catch (error) {
      console.error('Błąd podczas zatwierdzania zmian:', error);
      setError('Wystąpił błąd podczas zatwierdzania zmian');
    }
  }, [selectedEmployee, currentDate, currentUser, fetchShifts]);

  const formatValidationError = (error) => {
    if (error.response?.data?.detail) {
      if (Array.isArray(error.response.data.detail)) {
        return error.response.data.detail
          .map(err => err.msg)
          .join('\n');
      }
      return error.response.data.detail;
    }
    return 'Wystąpił błąd podczas zapisywania zmiany';
  };

  const handleShiftSubmit = async (formData) => {
    try {
      if (!formData.date || !formData.startTime || !formData.endTime || !formData.locationId) {
        setErrorMessage('Wszystkie pola są wymagane');
        return;
      }

      const userId = formData.employeeId || currentUser.uid;
      const shiftDate = format(formData.date, 'yyyy-MM-dd');

      // Sprawdź czy pracownik nie ma już zmiany w tym dniu
      const existingShifts = allShifts.filter(shift => 
        shift.user_id === userId && 
        shift.date === shiftDate &&
        (!selectedShift || shift.id !== selectedShift.id)
      );

      if (existingShifts.length > 0) {
        setErrorMessage('Ten pracownik ma już zaplanowaną zmianę w tym dniu');
        return;
      }

      // Określamy, czy zmiana powinna być automatycznie zatwierdzona
      // Tylko admin może tworzyć zatwierdzone zmiany
      const isAutoApproved = isAdmin;
      
      const shiftData = {
        date: shiftDate,
        start_time: formData.startTime,
        end_time: formData.endTime,
        location: locationNames[formData.locationId],
        user_id: userId,
        created_by_id: currentUser.uid,
        status: isAutoApproved ? ShiftStatus.APPROVED : ShiftStatus.PENDING,
        ...(isAutoApproved && { approved_by_id: currentUser.uid }),
        updated_at: new Date().toISOString(),
        // Dodajemy informację o roli, w której zmiana została utworzona
        created_by_role: userRole
      };

      if (selectedShift) {
        await updateSchedule(selectedShift.id, shiftData);
        setNeedsRefresh(true);
        
        try {
          // Dodaj log o aktualizacji zmiany
          await createLog({
            action: 'schedule_update',
            entity_type: 'schedule',
            entity_id: selectedShift.id,
            user_id: currentUser.uid,
            details: {
              old_data: selectedShift,
              new_data: { ...selectedShift, ...shiftData }
            }
          });
        } catch (logError) {
          console.error('Błąd podczas zapisywania logu:', logError);
        }

        setSuccessMessage('Zmiana została zaktualizowana');
        setShiftFormOpen(false);
        await fetchShifts(); // Od razu pobierz nowe dane
        return;
      }

      const newShift = await createSchedule(shiftData);
      setNeedsRefresh(true);

      try {
        // Dodaj log o utworzeniu zmiany
        await createLog({
          action: 'schedule_create',
          entity_type: 'schedule',
          entity_id: newShift.id,
          user_id: currentUser.uid,
          details: shiftData
        });
      } catch (logError) {
        console.error('Błąd podczas zapisywania logu:', logError);
      }

      setSuccessMessage('Zmiana została dodana');
      setShiftFormOpen(false);
      await fetchShifts(); // Od razu pobierz nowe dane
    } catch (error) {
      console.error('Błąd podczas zapisywania zmiany:', error);
      setError(formatValidationError(error));
      if (error.response?.status === 401) {
        logout();
      }
    }
  };

  // Filtruj i mapuj zmiany lokalnie
  const filteredShifts = useMemo(() => {
    return allShifts
      .filter(shift => {
        // Filtruj po lokalizacji
        if (selectedLocation && shift.location !== locationNames[selectedLocation]) {
          return false;
        }
        
        // Filtruj po pracowniku
        if (selectedEmployee && shift.user_id !== selectedEmployee) {
          return false;
        }
        
        return true;
      })
      .map(shift => {
        // Znajdź dane użytkownika
        const employee = employees.find(emp => emp.id === shift.user_id);
        return {
          ...shift,
          user: {
            uid: shift.user_id,
            first_name: employee ? employee.name.split(' ')[0] : '',
            last_name: employee ? employee.name.split(' ')[1] || '' : ''
          }
        };
      });
  }, [allShifts, selectedLocation, selectedEmployee, employees]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            mb: 3,
            fontFamily: 'Cinzel, serif',
            color: 'primary.main'
          }}
        >
          Grafik
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <ScheduleFilters
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            selectedEmployee={selectedEmployee}
            setSelectedEmployee={setSelectedEmployee}
            employees={employees}
            isAdmin={isAdmin}
          />
          {isAdmin && selectedEmployee && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleApproveAllMonth}
              sx={{ ml: 2 }}
            >
              Zatwierdź wszystkie zmiany
            </Button>
          )}
        </Box>
        
        <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {isLoading ? (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(33, 33, 33, 0.9)',
                backdropFilter: 'blur(4px)',
                zIndex: 1000
              }}
            >
              <CircularProgress size={60} sx={{ color: '#D4AF37' }} />
              <Typography sx={{ mt: 2, fontFamily: 'Montserrat', color: '#D4AF37', fontWeight: 500 }}>
                Pobieranie grafiku...
              </Typography>
            </Box>
          ) : null}
          <MonthlyCalendar 
            shifts={filteredShifts}
            onDayClick={handleDayClick}
            onShiftClick={handleShiftClick}
            onApproveShift={handleCalendarShiftApprove}
            onRejectShift={handleCalendarShiftReject}
            isAdmin={isAdmin}
            currentDate={currentDate}
            currentUser={currentUser}
            onMonthChange={handleMonthChange}
          />
        </Box>
      </Paper>

      {selectedDate && (
        <ShiftForm
          open={shiftFormOpen}
          onClose={() => {
            setShiftFormOpen(false);
            setError('');
          }}
          onSubmit={handleShiftSubmit}
          onDelete={handleShiftDelete}
          onApprove={handleShiftApprove}
          onReject={handleShiftReject}
          selectedDate={selectedDate}
          isAdmin={isAdmin}
          employees={employees}
          initialData={selectedShift}
          currentUser={currentUser}
          defaultLocation={selectedLocation}
        />
      )}

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError('')}
      >
        <Alert 
          onClose={() => setError('')} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
      >
        <Alert
          onClose={() => setErrorMessage('')}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Schedule;
